import {
  U21Chip,
  U21ChipProps,
  U21Typography,
} from 'app/shared/u21-ui/components';
import { IconCircleX, IconInfoCircle, IconHourglass } from '@u21/tabler-icons';
import { toggleSidebar } from 'app/modules/sidebar/slice';
import { SidebarComponentTypes } from 'app/modules/sidebar/models';
import { useDispatch } from 'react-redux';
import {
  AIRecommendation,
  AIResultType,
} from 'app/modules/aiAgentResults/models';

interface AiAgentRecommendationChipProps {
  clickType?: 'ALERT' | 'RECOMMENDATION';
  id: string;
  type: AIResultType;
  value: AIRecommendation;
}

const AI_RECOMMENDATION_CONFIG: Record<
  NonNullable<AIRecommendation> | 'EMPTY',
  U21ChipProps
> = {
  FALSE_POSITIVE: {
    children: 'Close as false positive',
    icon: <IconCircleX />,
    color: 'success',
    variant: 'filled',
    tooltip:
      'There is sufficient information provided on the alert summary to close the alert without a need for further action.',
  },
  UNDETERMINED: {
    children: 'Human review requested',
    icon: <IconInfoCircle />,
    color: 'info',
    variant: 'ghost',
    tooltip: 'This alert requires further review before closing.',
  },
  EMPTY: {
    children: 'Awaiting recommendation',
    icon: <IconHourglass />,
    color: 'default',
    variant: 'outlined',
    tooltip: (
      <p>
        A recommendation will be made when a review is completed.
        <U21Typography variant="caption" color="text.secondary">
          <i>
            Note: Reviews completed prior to October 8th, 2025 will not have
            recommendations.
          </i>
        </U21Typography>
      </p>
    ),
  },
};

export const AiAgentRecommendationChip = ({
  clickType,
  id,
  type,
  value,
}: AiAgentRecommendationChipProps) => {
  const dispatch = useDispatch();
  const configKey = value || 'EMPTY';
  const config = AI_RECOMMENDATION_CONFIG[configKey];

  if (!config) {
    return <U21Chip variant="ghost">Unknown</U21Chip>;
  }

  const clickable = clickType && configKey !== 'EMPTY';
  const showEndIcon = clickable && configKey !== 'UNDETERMINED';

  return (
    <U21Chip
      {...config}
      endIcon={showEndIcon ? <IconInfoCircle /> : undefined}
      onClick={
        clickable
          ? (e) => {
              e.stopPropagation();
              if (clickType === 'ALERT') {
                dispatch(
                  toggleSidebar({
                    type: SidebarComponentTypes.ALERT,
                    data: {
                      alert: { id: Number(id) },
                      scrollTo: 'AI_RECOMMENDATION',
                    },
                    coordinates: { x: e.clientX, y: e.clientY },
                  }),
                );
              } else if (clickType === 'RECOMMENDATION') {
                dispatch(
                  toggleSidebar({
                    type: SidebarComponentTypes.AI_RECOMMENDATION,
                    data: { id, type },
                    coordinates: { x: e.clientX, y: e.clientY },
                  }),
                );
              }
            }
          : undefined
      }
    />
  );
};
